import { DefaultProfilePicture } from "../../../api/src/shared/domain/user";
import certifiedIconSvg from "../assets/icons/lemon-certified.svg";
import { Avatar, type AvatarSize } from "./ui/Avatar";
import { cn } from "../lib/utils";
import { ComponentPropsWithoutRef, type ReactNode } from "react";

export type ProfilePictureUser = {
  lemonCertified: boolean | null;
  profilePicture: {
    image: { path: string; url: string } | null;
    name: DefaultProfilePicture | null;
  } | null;
  fullname: string | null;
};

type Props = ComponentPropsWithoutRef<"div"> & {
  user: ProfilePictureUser;
  height?: string | number; // TODO: Change for "size"
  /** Corner action/status overlay (e.g. edit camera). Shown bottom-right. */
  badge: ReactNode | null;
};

// Map the legacy pixel height to the nearest of sm/md/lg (24/38/52px; midpoints
// at 31 and 45). The mobile callers asking for 64 to 96 have always rendered lg
// and keep doing so: only 128 and up, asked by the desktop profile header alone,
// reaches its 64px hero avatar (Figma "05 - Profil").
// Non-numeric heights (e.g. "100%") fall back to lg.
function heightToAvatarSize(height: string | number | undefined): AvatarSize {
  if (typeof height !== "number") return "lg";
  if (height <= 31) return "sm";
  if (height <= 45) return "md";
  if (height < 128) return "lg";
  return "hero";
}

export function getInitials(fullname: string | null): string {
  if (fullname === null) return "";
  const trimmed = fullname.trim();
  if (trimmed.length === 0) return "";
  const words = trimmed.split(/\s+/);
  const first = words[0];
  if (first === undefined) return "";
  const second = words[1];
  const secondInitial = second === undefined ? "" : second.charAt(0);
  return (first.charAt(0) + secondInitial).toUpperCase();
}

export default function ProfilePicture(props: Props) {
  const { user, height, className, style, badge } = props;
  const size = heightToAvatarSize(height);
  const image = user.profilePicture === null ? null : user.profilePicture.image;

  return (
    <div className={cn("relative w-fit", className)} style={style}>
      {image !== null ? (
        <Avatar
          variant="image"
          src={image.url}
          alt={user.fullname === null ? "" : user.fullname}
          imageFit="cover"
          size={size}
          badge={badge}
        />
      ) : (
        <Avatar
          variant="initials"
          initials={getInitials(user.fullname)}
          size={size}
          badge={badge}
        />
      )}

      {user.lemonCertified === true ? (
        <img
          className="pointer-events-none absolute top-[-5%] right-[-5%] size-2/5 "
          src={certifiedIconSvg}
          alt=""
        />
      ) : null}
    </div>
  );
}
